/**
 * Shape of a single `tenancy.pro_studio_performance_metrics` row's `settings`
 * column as written by the perf bench harness
 * (see `playwright/tests/perf/workflows-table.bench.ts` and the
 * `upload-perf-metrics.mjs` script).
 *
 * Hasura returns `settings` as `jsonb` — Hasura types it loosely. We narrow
 * it here so the page can render strongly-typed fields without scattering
 * `as unknown as ...` casts.
 */

export type PerfHeapMetrics = {
  jsHeapUsedSize?: number;
  jsHeapTotalSize?: number;
  documents?: number;
  nodes?: number;
  jsEventListeners?: number;
  layoutCount?: number;
  recalcStyleCount?: number;
};

export type PerfCpuProfile = {
  totalMs?: number;
  scriptingMs?: number;
  samples?: number;
};

export type PerfFpsSummary = {
  samples?: number;
  meanFps?: number;
  minFps?: number;
  p95FrameTimeMs?: number;
  longFrames?: number;
};

export type PerfStoreStats = {
  totalRows?: number;
  visibleRows?: number;
  domRows?: number;
  windowedRows?: number;
  virtualWindowStart?: number;
  virtualWindowEnd?: number;
};

export type PerfMetricCi = {
  sha?: string | null;
  ref?: string | null;
  branch?: string | null;
  runId?: string | null;
  repository?: string | null;
  eventName?: string | null;
  prNumber?: number | null;
};

export type PerfMetricMeta = {
  file?: string;
  type?: string;
  uploadedAt?: string;
  ci?: PerfMetricCi;
};

export type PerfMetricSettings = {
  commit?: string;
  size?: number;
  scenario?: string;
  wallMs?: number;
  before?: PerfHeapMetrics;
  after?: PerfHeapMetrics;
  metrics?: PerfHeapMetrics;
  delta?: PerfHeapMetrics;
  cpu?: PerfCpuProfile;
  fps?: PerfFpsSummary;
  domRows?: number;
  heapSnapshotBytes?: number;
  storeStats?: PerfStoreStats;
  __meta?: PerfMetricMeta;
};

export type PerfMetricRow = {
  id: string;
  type: string;
  settings: PerfMetricSettings | null;
  createdAt: string;
  updatedAt?: string | null;
};

export const ALL_SCENARIOS = [
  'cold-render',
  'scroll',
  'single-drag',
  'search-typing',
  'idle-snapshot',
] as const;
export type Scenario = (typeof ALL_SCENARIOS)[number];

// ChartMetricKey is DERIVED from CHART_METRICS (below) so the union and the
// registry can't drift apart — adding a metric is one edit, and a key without
// a registry entry is impossible by construction.
export const CHART_METRICS = [
  {
    key: 'jsHeapUsedSize',
    label: 'JS heap used',
    unit: 'MB',
    pick: (s) => {
      const v = s?.after?.jsHeapUsedSize ?? s?.metrics?.jsHeapUsedSize ?? null;
      return v == null ? null : v / 1024 / 1024;
    },
  },
  {
    // Total DOM node count (after the scenario) — the headline structural
    // metric for the lazy-cell-control work. Unlike `domRows` (the rendered
    // <tr> count, fixed by row virtualization) this reflects per-row weight, so
    // it's where the lazy-controls reduction is actually visible.
    key: 'domNodes',
    label: 'DOM nodes',
    unit: '',
    pick: (s) => s?.after?.nodes ?? null,
  },
  {
    key: 'scriptingMs',
    label: 'Scripting CPU',
    unit: 'ms',
    pick: (s) => s?.cpu?.scriptingMs ?? null,
  },
  {
    key: 'meanFps',
    label: 'Mean FPS',
    unit: 'fps',
    pick: (s) => s?.fps?.meanFps ?? null,
  },
  {
    key: 'domRows',
    label: 'DOM rows',
    unit: '',
    pick: (s) => s?.domRows ?? null,
  },
  {
    key: 'wallMs',
    label: 'Wall time',
    unit: 'ms',
    pick: (s) => s?.wallMs ?? null,
  },
] as const satisfies ReadonlyArray<{
  key: string;
  label: string;
  unit: string;
  pick: (s: PerfMetricSettings | null | undefined) => number | null;
}>;

export type ChartMetricKey = (typeof CHART_METRICS)[number]['key'];
